import { useCallback, useEffect, useState } from 'react'

/**
 * Modo claro y oscuro, conmutable.
 *
 * Hasta ahora seguía al sistema y no se podía cambiar. José lo pidió el 15 de
 * octubre: «yo lo prefiero en modo claro, pero déjalo con la lunita para poder
 * cambiarlo». Así que hay tres opciones y «sistema» sigue siendo la de salida,
 * que es lo que menos sorprende a quien no toca nada.
 *
 * La clase `oscuro` va en el <html> y es la que activa todas las variantes
 * `dark:` de Tailwind (ver el @custom-variant de estilos.css).
 */
export type Tema = 'sistema' | 'claro' | 'oscuro'

const CLAVE = 'codoh:tema'

const esTema = (valor: unknown): valor is Tema =>
  valor === 'sistema' || valor === 'claro' || valor === 'oscuro'

export const temaGuardado = (): Tema => {
  try {
    const guardado = localStorage.getItem(CLAVE)
    return esTema(guardado) ? guardado : 'sistema'
  } catch {
    // Navegador con el almacenamiento bloqueado: se sigue con el del sistema.
    return 'sistema'
  }
}

const prefiereOscuro = (): boolean =>
  window.matchMedia('(prefers-color-scheme: dark)').matches

/**
 * Pinta el tema. Se llama también desde main.tsx antes de montar React, para que
 * no se vea el fogonazo blanco de un fondo que va a ser oscuro.
 */
export const aplicarTema = (tema: Tema): void => {
  const oscuro = tema === 'oscuro' || (tema === 'sistema' && prefiereOscuro())

  document.documentElement.classList.toggle('oscuro', oscuro)
  // Para que los desplegables y las barras de desplazamiento nativas vayan igual.
  document.documentElement.style.colorScheme = oscuro ? 'dark' : 'light'
}

export function useTema(): { tema: Tema; cambiar: (tema: Tema) => void; esOscuro: boolean } {
  const [tema, setTema] = useState<Tema>(temaGuardado)

  useEffect(() => {
    aplicarTema(tema)

    if (tema !== 'sistema') return

    // Con «sistema», si el ordenador cambia de modo a media tarde, se sigue.
    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    const alCambiar = () => aplicarTema('sistema')
    consulta.addEventListener('change', alCambiar)

    return () => consulta.removeEventListener('change', alCambiar)
  }, [tema])

  const cambiar = useCallback((nuevo: Tema) => {
    try {
      localStorage.setItem(CLAVE, nuevo)
    } catch {
      // Da igual: el tema se aplica, lo que no se puede es recordarlo.
    }
    setTema(nuevo)
  }, [])

  const esOscuro =
    tema === 'oscuro' || (tema === 'sistema' && typeof window !== 'undefined' && prefiereOscuro())

  return { tema, cambiar, esOscuro }
}
